import { useEffect, useState, useCallback } from 'react';
import {
  useUserInactivityStore,
  getSessionExpiryMinutes,
} from 'src/stores/useUserInactivityStore';
import { captureSessionMessage } from 'src/utils/sentry/sessionSentry';

const INACTIVITY_CHECK_INTERVAL_MS = 1000;

export interface UseUserInactivityReturn {
  /** True when user has been inactive for the configured period (e.g. 15 minutes). */
  isInactivityWarningVisible: boolean;
  /** Reset inactivity timer (e.g. when user clicks "Continue Session"). */
  resetInactivity: () => void;
  /** When true, automatic token refresh should be paused. */
  shouldPauseTokenRefresh: boolean;
  /** Configured inactivity timeout in minutes. */
  inactivityTimeoutMinutes: number;
}

/**
 * Tracks user inactivity based on click events.
 * Shows inactivity warning after VITE_UI_USER_INACTIVE_WARNING_IN_MINUTES (default 15).
 * Activity is any click on the document.
 */
export function useUserInactivity(): UseUserInactivityReturn {
  const lastActivityAt = useUserInactivityStore((s) => s.lastActivityAt);
  const { recordActivity, resetInactivity } = useUserInactivityStore(
    (s) => s.actions,
  );

  const [now, setNow] = useState(() => Date.now());
  const inactivityTimeoutMinutes = getSessionExpiryMinutes();
  const expiryMs = inactivityTimeoutMinutes * 60 * 1000;

  const [isInactivityWarningVisible, setIsInactivityWarningVisible] =
    useState(false);
  const [shouldPauseTokenRefresh, setShouldPauseTokenRefresh] = useState(false);

  // Tick every second to re-evaluate inactivity
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, INACTIVITY_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // Update visibility flags when time passes
  useEffect(() => {
    // Only check if we are NOT currently warning
    // Once warning is shown, it stays shown until explicitly reset
    if (!isInactivityWarningVisible) {
      if (now - lastActivityAt >= expiryMs) {
        setIsInactivityWarningVisible(true);
        setShouldPauseTokenRefresh(true);
        captureSessionMessage(
          'UI inactivity warning became visible',
          'warning',
          {
            event: 'session.inactivity.warningVisible',
            extra: {
              inactivityTimeoutMinutes,
              idleForMs: now - lastActivityAt,
              thresholdMs: expiryMs,
            },
          },
        );
      }
    }
  }, [
    now,
    lastActivityAt,
    expiryMs,
    isInactivityWarningVisible,
    inactivityTimeoutMinutes,
  ]);

  // Record activity on any interaction (click, mouse move, key press, scroll)
  useEffect(() => {
    let lastRecorded = 0;

    const handleActivity = (event: Event) => {
      // If the warning is already visible, ignore all generic activity.
      // E.g., moving the mouse or randomly clicking shouldn't reset the timer or dismiss the modal.
      if (isInactivityWarningVisible) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      // Also ignore interactions inside the inactivity warning itself
      if (target?.closest('[data-inactivity-warning="true"]')) {
        return;
      }

      const nowMs = Date.now();
      // Throttle activity recording to at most once every 2 seconds
      // to avoid spamming the Zustand store and causing excessive re-renders
      if (nowMs - lastRecorded > 2000) {
        lastRecorded = nowMs;
        recordActivity();
      }
    };

    const events = ['click', 'mousemove', 'keydown', 'scroll', 'wheel'];

    events.forEach((eventName) => {
      document.addEventListener(eventName, handleActivity, true);
    });

    return () => {
      events.forEach((eventName) => {
        document.removeEventListener(eventName, handleActivity, true);
      });
    };
  }, [isInactivityWarningVisible, recordActivity]);

  const reset = useCallback(() => {
    captureSessionMessage('UI inactivity warning reset', 'info', {
      event: 'session.inactivity.reset',
      extra: {
        inactivityTimeoutMinutes,
        idleForMs: Date.now() - lastActivityAt,
      },
    });
    setIsInactivityWarningVisible(false);
    setShouldPauseTokenRefresh(false);
    resetInactivity();
    setNow(Date.now());
  }, [resetInactivity, inactivityTimeoutMinutes, lastActivityAt]);

  return {
    isInactivityWarningVisible,
    resetInactivity: reset,
    shouldPauseTokenRefresh,
    inactivityTimeoutMinutes,
  };
}

export default useUserInactivity;
